import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { toast } from "sonner";
import { ArrowLeft, LogOut, KeyRound, Send, Mail, Monitor, Clock, Search, CheckCircle2, AlertTriangle, PauseCircle } from "lucide-react";
import TenantBrand from "@/components/TenantBrand";
import { parseDelivered } from "@/lib/deliveredFormat";

const STATUS_STYLE = {
  resuelto: { border: "border-l-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  pendiente: { border: "border-l-amber-500", badge: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
};
const REPLACEMENT_PREFIXES = ["Reemplazo: ", "Premio Ruleta: "];

export default function Codigos() {
  const { logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [plataforma, setPlataforma] = useState("");
  const [correo, setCorreo] = useState("");
  const [compraSel, setCompraSel] = useState(null);

  const load = () => api.get("/my/codes").then((r) => setRequests(r.data)).catch(() => {});
  const loadPurchases = () => api.get("/my/purchases").then((r) => setPurchases(r.data)).catch(() => {});
  useEffect(() => { load(); loadPurchases(); }, []);

  // Mismas cuentas realmente compradas por el cliente que ya se usan en
  // Reportes -- así elige de una lista real en vez de escribir el correo a
  // mano, sin duplicar el flujo de "elegir plataforma primero".
  const misCompras = useMemo(() => {
    const list = [];
    purchases.forEach((s) => {
      (s.items || []).forEach((it) => {
        const matchedPrefix = REPLACEMENT_PREFIXES.find((pre) => it.product_name?.startsWith(pre));
        const productName = matchedPrefix ? it.product_name.slice(matchedPrefix.length) : it.product_name;
        (it.delivered && it.delivered.length ? it.delivered : [""]).forEach((d, di) => {
          const p = d ? parseDelivered(d) : {};
          list.push({
            key: `${s.id}-${it.product_id}-${di}`, sale_id: s.id, product_name: productName,
            correo: p.texto || p.correo || d, fecha: s.created_at,
          });
        });
      });
    });
    return list.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [purchases]);

  const misPlataformas = useMemo(() => {
    const seen = new Set();
    const list = [];
    misCompras.forEach((c) => { if (!seen.has(c.product_name)) { seen.add(c.product_name); list.push(c.product_name); } });
    return list;
  }, [misCompras]);

  const cuentasDePlataforma = useMemo(
    () => misCompras.filter((c) => c.product_name === plataforma),
    [misCompras, plataforma]
  );

  const onPlataformaSelect = (e) => {
    const val = e.target.value;
    setPlataforma(val);
    setCompraSel(null);
    setCorreo("");
  };
  const pickCompra = (c) => { setCompraSel(c); setCorreo(c.correo); };

  const pausado = !!tenant?.codigos_pausados;

  const submit = async () => {
    if (!correo.trim()) return toast.error("Escribe o elige el correo de la cuenta");
    setBusy(true);
    try {
      await api.post("/codes/request", { correo_cuenta: correo.trim(), plataforma, sale_id: compraSel?.sale_id || null });
      toast.success("Solicitud enviada -- te avisamos en cuanto tengamos tu código");
      setCorreo(""); setPlataforma(""); setCompraSel(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-primary text-white shadow-md">
        <div className="max-w-3xl mx-auto px-4 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3"><TenantBrand tenant={tenant} imgClassName="h-10 w-auto object-contain" textClassName="font-head text-lg font-extrabold uppercase tracking-tight text-current" /><span className="font-head text-lg font-bold">Pedir Código</span></div>
          <div className="flex items-center gap-2">
            <button data-testid="codigos-back" onClick={() => navigate("/compras")} className="w-10 h-10 grid place-items-center rounded-lg bg-white/15 hover:bg-white/25 transition-colors"><ArrowLeft size={18} /></button>
            <button data-testid="codigos-logout" onClick={() => { logout(); navigate("/login"); }} className="w-10 h-10 grid place-items-center rounded-lg bg-white/15 hover:bg-white/25 transition-colors"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-100"><KeyRound size={22} className="text-primary" /><h3 className="font-head text-xl font-bold text-slate-900">Pedir código de acceso</h3></div>
          <p className="text-sm text-slate-500 mb-5">Para Netflix, Disney+, Fox One, Apple TV y otras cuentas que piden un código de verificación al iniciar sesión: pide aquí el código y te avisamos apenas lo tengamos.</p>

          {pausado ? (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3.5 text-amber-800">
              <PauseCircle size={18} className="shrink-0 mt-0.5" />
              <p className="text-sm font-semibold">{tenant.codigos_pausados_mensaje || "La sección de códigos está pausada por ahora -- vuelve a intentarlo más tarde."}</p>
            </div>
          ) : (
            <div className="space-y-5">
              <RField icon={Monitor} label="Plataforma (opcional)">
                <select data-testid="cod-plataforma" value={plataforma} onChange={onPlataformaSelect} className={ic()}>
                  <option value="">Selecciona el servicio...</option>
                  {misPlataformas.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </RField>

              {plataforma && cuentasDePlataforma.length > 0 && (
                <div className="space-y-1.5">
                  {cuentasDePlataforma.map((c) => (
                    <button type="button" key={c.key} onClick={() => pickCompra(c)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${compraSel?.key === c.key ? "border-primary bg-primary/5 text-primary font-semibold" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                      {c.correo}
                    </button>
                  ))}
                </div>
              )}

              <RField icon={Mail} label="Correo de la cuenta">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="cod-correo" type="email" value={correo}
                    onChange={(e) => { setCompraSel(null); setCorreo(e.target.value); }}
                    placeholder="Pega o escribe el correo de la cuenta..." className={`pl-10 ${ic()}`} />
                </div>
              </RField>

              <button data-testid="cod-submit" disabled={busy} onClick={submit} className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-lg py-3.5 transition-colors"><Send size={18} /> Pedir código</button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 pb-4 mb-4 border-b border-slate-100"><KeyRound size={22} className="text-primary" /><h3 className="font-head text-xl font-bold text-slate-900">Mis solicitudes</h3></div>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 grid place-items-center mx-auto text-slate-400"><KeyRound size={26} /></div>
              <p className="font-head font-bold text-slate-700 mt-3">Sin solicitudes</p>
              <p className="text-sm text-slate-400 mt-1">Cuando pidas un código, aparecerá aquí.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => {
                const style = STATUS_STYLE[r.status] || STATUS_STYLE.pendiente;
                const StatusIcon = style.icon;
                return (
                  <div key={r.id} data-testid={`mycode-${r.id}`} className={`border border-slate-200 border-l-4 rounded-xl p-4 ${style.border}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-9 h-9 shrink-0 rounded-lg bg-primary/10 grid place-items-center text-primary"><Monitor size={16} /></span>
                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 truncate">{r.plataforma || "Cuenta"}</p>
                          <p className="text-xs text-slate-500 truncate">{r.correo_cuenta}</p>
                        </div>
                      </div>
                      <span className={`shrink-0 flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border uppercase tracking-wide ${style.badge}`}>
                        <StatusIcon size={12} /> {r.status}
                      </span>
                    </div>
                    {r.status === "resuelto" && r.code && (
                      <div className="mt-3 bg-slate-900 rounded-lg p-3 text-center">
                        <p className="text-xs font-bold uppercase tracking-wide text-emerald-400 mb-1">Tu código</p>
                        <p className="font-head text-2xl font-extrabold text-white tracking-widest">{r.code}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ic() { return "w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-colors"; }
function RField({ icon: Icon, label, children }) {
  return <label className="block"><span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600 mb-1.5"><Icon size={13} className="text-primary" /> {label}</span>{children}</label>;
}
